import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import AccountTypeSignupFields from '../auth/AccountTypeSignupFields';
import {
  registerPersonaProfile,
  validatePersonaVerification,
} from '../../utils/personaRegistration';
import { isSignupPersonaSelectable } from '../../constants/personas';

export default function OnboardingModal() {
  const { t, language } = useLanguage();
  const { currentUser, userProfile, profileLoading, refreshUserProfile } = useAuth();
  const [selectedPersona, setSelectedPersona] = useState('investisseur');
  const [verification, setVerification] = useState({
    companyName: '',
    phone: '',
    street: '',
    city: '',
    province: '',
    postalCode: '',
    country: 'Canada',
  });
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const showOnboarding =
    currentUser &&
    !profileLoading &&
    (!userProfile || userProfile.onboardingCompleted !== true);
  if (!showOnboarding) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPersona) return;
    setError(null);
    if (!acceptedLegal) {
      setError(t('signup.legal.required'));
      return;
    }
    if (!isSignupPersonaSelectable(selectedPersona)) {
      setError(t('signup.accountType.notAvailable'));
      return;
    }
    const validationKey = validatePersonaVerification(selectedPersona, verification);
    if (validationKey) {
      setError(t(validationKey));
      return;
    }
    setSubmitting(true);
    try {
      await registerPersonaProfile({
        uid: currentUser.uid,
        email: currentUser.email || '',
        persona: selectedPersona,
        displayName: currentUser.displayName || '',
        locale: language === 'en' ? 'en' : 'fr',
        verification,
        legalAccepted: true,
      });
      if (refreshUserProfile) await refreshUserProfile();
    } catch (err) {
      console.error('Onboarding error:', err);
      const msg = err?.message;
      setError(msg && t(msg) !== msg ? t(msg) : msg || t('profile.saveError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[220] rentalyzer-modal-overlay flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold text-gray-900 mb-1">
          {t('onboarding.title')}
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          {t('onboarding.question')}
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <AccountTypeSignupFields
            selectedPersona={selectedPersona}
            onPersonaChange={setSelectedPersona}
            verification={verification}
            onVerificationChange={setVerification}
            disabled={submitting}
          />
          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={acceptedLegal}
              onChange={(e) => setAcceptedLegal(e.target.checked)}
              className="mt-1 shrink-0"
              required
            />
            <span>
              {t('signup.legal.acceptPrefix')}{' '}
              <Link to="/terms" className="text-blue-600 hover:underline">
                {t('legal.terms')}
              </Link>{' '}
              {t('signup.legal.acceptAnd')}{' '}
              <Link to="/privacy" className="text-blue-600 hover:underline">
                {t('legal.privacy')}
              </Link>
              {t('signup.legal.acceptSuffix')}
            </span>
          </label>
          {error ? (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={!selectedPersona || submitting}
            className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700"
          >
            {submitting ? t('loading') : t('onboarding.continue')}
          </button>
        </form>
      </div>
    </div>
  );
}
